// @flow

import React from 'react';
import {Field, Fields, reduxForm} from 'redux-form';
import TextAreaFormInput from '../TextAreaFormInput/TextAreaFormInput';
import SelectFormInput from '../SelectFormInput/SelectFormInput';
import OrderApartmentInput from '../OrderApartmentInput/OrderApartmentInput';
import FormattedPrice from '../FormattedPrice/FormattedPrice';
import {
  NEW,
  COMPLETED,
  CONFIRMED,
  states,
} from '../../orderStates.js';
import DecimalFormInput from '../DecimalFormInput/DecimalFormInput';
import SpecialProductsInput from '../SpecialProductsInput/SpecialProductsInput';
import {apiBase} from '../../helpers/request';
import {getUrl} from '../../containers/PurchaseOrderReportContainer/PurchaseOrderReportContainer';
import ActionConfirmation from '../../containers/ActionConfirmation/ActionConfirmation';
import booleanValues from '../../booleanValues';
import BasicFormInput from '../BasicFormInput/BasicFormInput';
import _ from 'lodash/fp';
import './OrderForm.css';

const fileUrl = `${apiBase}/files`;

export type Props = {
  orderComplete: boolean,
  orderTotalPrice: number,
  updateOrder: Function,
  updateOrderRows: Function,
  clearOrderRows: Function,
  apartments: Object,
  projects: any[],
  users: any[],
  salesUsers: any[],
  specialProducts: Object,
  productTypes: any[],
  order: ?any,
  onOpenCustomerOrder?: Function,
  onOpenCustomerApartment?: Function,
  onUserChange: Function,
  suppliers: any[],
  reportFilter: {
    includePlans: boolean,
    supplierId: ?number,
  },
  updateReportFilter: Function,
  userOrderFilter: {
    includePlans: boolean,
  },
  updateUserOrderFilter: Function,
};

const OrderForm = (props: Props) => {
  const {
    orderComplete,
    updateOrder,
    updateOrderRows,
    clearOrderRows,
    apartments,
    projects,
    users,
    order,
    specialProducts,
    productTypes,
    onOpenCustomerOrder,
    onOpenCustomerApartment,
    salesUsers,
    onUserChange,
    suppliers,
    reportFilter,
    updateReportFilter,
    userOrderFilter,
    updateUserOrderFilter,
    orderTotalPrice,
  } = props;
  let userList = users;
  if (order && !!order.user) {
    userList = [
      ...userList,
      order.user,
    ];
  }

  return (
    <div className="OrderForm">
      <form>
        <div className="row">
          <div className="small-6 column">
            <Fields
              names={['apartment', 'apartmentId', 'projectId']}
              change={onUserChange}
              component={OrderApartmentInput}
              disabled={!!order}
              apartments={apartments}
              projects={projects} />
          </div>
          <div className="small-6 column">
            <div>
              <Field
                name="userId"
                label="Kund"
                normalize={value => value ? value.id : null}
                onChange={onUserChange}
                disabled={!!order}
                component={SelectFormInput}
                valueKey="id"
                labelKey="username"
                options={userList} />
              <Field
                name="state"
                label="Status"
                normalize={value => value ? value.id : null}
                onChange={onUserChange}
                disabled={true}
                component={SelectFormInput}
                valueKey="id"
                labelKey="label"
                options={states} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="small-6 column">
            <Field
              name="discount"
              label="Rabatt"
              onChange={onUserChange}
              component={DecimalFormInput} />
          </div>
          {!!order &&
            <div className="small-6 column">
              <label>
                Totalpris (efter ev. rabatt)
              </label>
              <div className="readonly-text-control">
                <FormattedPrice value={orderTotalPrice} />
              </div>
            </div>
          }
        </div>
        <div className="row">
          <div className="small-6 column">
            <Field
              type="text"
              name="discountComment"
              label="Rabattkommentar"
              onChange={onUserChange}
              component={BasicFormInput} />
          </div>
          <div className="small-6 column">
          </div>
        </div>
        <div className="row">
          <div className="small-6 column">
            <Field
              type="text"
              name="additionalCustomerName"
              label="Ytterliggare kund - namn"
              onChange={onUserChange}
              component={BasicFormInput} />
          </div>
          <div className="small-6 column">
            <Field
              type="text"
              name="additionalCustomerEmail"
              label="Ytterliggare kund - email"
              onChange={onUserChange}
              component={BasicFormInput} />
          </div>
        </div>
        <div className="row">
          <div className="small-6 column">
            <Field
              type="text"
              name="additionalCustomerPhone"
              label="Ytterliggare kund - telefon"
              onChange={onUserChange}
              component={BasicFormInput} />
          </div>
          <div className="small-6 column">
            <Field
              type="text"
              name="additionalCustomerMobile"
              label="Ytterliggare kund - mobiltelefon"
              onChange={onUserChange}
              component={BasicFormInput} />
          </div>
        </div>
        <div className="row">
          <div className="small-6 column">
            <Field
              name="salesUserId"
              label="Säljare"
              normalize={value => value ? value.id : null}
              onChange={onUserChange}
              component={SelectFormInput}
              valueKey="id"
              labelKey="username"
              options={salesUsers} />
          </div>

          {!!order &&
            <div className="small-6 column">
              <label>
                &nbsp;
              </label>
              {!orderComplete &&
                <ActionConfirmation
                  id="fillStandard"
                  className="expanded warning column shrink"
                  message="Vill du verkligen fylla resterande produktplatser med standardval?"
                  buttonText="Fyll med standardval"
                  action={() => updateOrderRows(order.id, {standard: true})} />
              }
              {order.state === NEW && !!order.rows.length &&
                <ActionConfirmation
                  id="clearRows"
                  className="expanded warning column shrink"
                  message="Vill du verkligen rensa alla val som redan gjorts?"
                  buttonText="Rensa alla val"
                  action={() => clearOrderRows(order.id)} />
              }
              {order.state === NEW &&
                <ActionConfirmation
                  id="lockOrder"
                  className="expanded success column shrink"
                  message="Vill du verkligen låsa beställningen?"
                  buttonText="Lås beställning"
                  action={() => updateOrder(order.id, {state: COMPLETED})}
                  disabled={!orderComplete} />
              }
              {order.state === COMPLETED &&
                <ActionConfirmation
                  id="unlockOrder"
                  className="expanded alert column shrink"
                  message="Vill du verkligen låsa upp beställningen?"
                  buttonText="Lås upp beställning"
                  action={() => updateOrder(order.id, {state: NEW})} />
              }
              {order.state === COMPLETED &&
                <ActionConfirmation
                  id="confirmPurchase"
                  className="expanded success column shrink"
                  messages={[
                    'Är de aktuella plan- och köksritningarna kopplade?',
                    'Vill du verkligen bekräfta beställningen för inköp?',
                  ]}
                  buttonText="Bekräfta för inköp"
                  action={() => updateOrder(order.id, {state: CONFIRMED})} />
              }
              {order.state === CONFIRMED &&
                <ActionConfirmation
                  id="cancelPurchase"
                  className="expanded alert column shrink"
                  message="Vill du verkligen avbryta beställningen för inköp?"
                  buttonText="Avbryt för inköp"
                  action={() => updateOrder(order.id, {state: COMPLETED})} />
              }
            </div>
          }
        </div>

        <div className="row">
          {!!onOpenCustomerApartment &&
          <div className="small-6 column">
            <button
              onClick={() => onOpenCustomerApartment && onOpenCustomerApartment()}
              type="button"
              className="expanded button">
                Öppna lägenhet
            </button>
          </div>
          }
          {!!onOpenCustomerOrder &&
            <div className="small-6 column">
              <button
                onClick={() => onOpenCustomerOrder && onOpenCustomerOrder()}
                type="button"
                className="expanded button">
                Öppna kundorder
              </button>
            </div>
          }
        </div>

        <div className="row">
          <div className="small-6 column">
            <Field
              name="specialProducts"
              label="Specialprodukter"
              onChange={onUserChange}
              component={SpecialProductsInput}
              specialProducts={specialProducts}
              productTypes={productTypes} />
          </div>

        </div>

        <div className="row">
          <div className="small-6 column">
            <Field
              name="comment"
              label="Beställningskommentar"
              onChange={onUserChange}
              component={TextAreaFormInput} />
          </div>
          {!!order &&
            <div className="small-6 column">
              <label>
                Beställningsunderlag
              </label>
              <SelectFormInput
                input={{
                  value: !!userOrderFilter.includePlans,
                  onBlur: _.noop,
                  onChange: includePlans => updateUserOrderFilter(includePlans ? includePlans.value : false),
                }}
                label="Inkludera ritningar"
                meta={{}}
                valueKey="value"
                labelKey="label"
                clearable={false}
                options={booleanValues} />
              <div className="row no-row-margins expand FileDropzone">
                <a
                  href={`${fileUrl}/export/orders/${order.id}/user/order_${order.id}_user_export.pdf${!!userOrderFilter.includePlans ? '?withPlans=1' : ''}`}
                  target="_blank"
                  className="file column text-truncate">
                  Hämta beställningsunderlag
                </a>
                <a
                  href={`${fileUrl}/export/orders/${order.id}/user${!!userOrderFilter.includePlans ? '?withPlans=1' : ''}`}
                  target="_blank"
                  className="button column shrink">
                  <i className="fa fa-download"></i>
                </a>
              </div>
              {order.state > COMPLETED &&
                <div>
                  <label>
                    Inköpsrapport
                  </label>
                  <SelectFormInput
                    input={{
                      value: reportFilter.supplierId,
                      onBlur: _.noop,
                      onChange: supplier => updateReportFilter(supplier ? supplier.id : null, reportFilter.includePlans),
                    }}
                    placeholder="Välj leverantör"
                    meta={{}}
                    valueKey="id"
                    labelKey="name"
                    multi={false}
                    options={suppliers}
                    clearable={true} />
                  <SelectFormInput
                    input={{
                      value: !!reportFilter.includePlans,
                      onBlur: _.noop,
                      onChange: includePlans => updateReportFilter(reportFilter.supplierId, includePlans ? includePlans.value : false),
                    }}
                    label="Inkludera ritningar"
                    meta={{}}
                    valueKey="value"
                    labelKey="label"
                    clearable={false}
                    options={booleanValues} />
                  <div className="row no-row-margins expand FileDropzone">
                    <a
                      href={getUrl(order.apartment.projectId, order.apartmentId, reportFilter.supplierId, true, reportFilter.includePlans)}
                      target="_blank"
                      className="file column text-truncate">
                      Hämta inköpsrapport
                    </a>
                    <a
                      href={getUrl(order.apartment.projectId, order.apartmentId, reportFilter.supplierId, false, reportFilter.includePlans)}
                      target="_blank"
                      className="button column shrink">
                      <i className="fa fa-download"></i>
                    </a>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </form>
    </div>
  );
};

export default reduxForm({
  form: 'order',
  enableReinitialize: true,
})(OrderForm);



// WEBPACK FOOTER //
// ./src/components/OrderForm/OrderForm.js